'use client';

import * as React from 'react';

import { isThemeName, THEME_NAMES, type ThemeName } from './themes/registry';

const DESIGN_THEME_STORAGE_KEY = 'metropolis-design-theme';

function readStoredTheme(fallback: ThemeName): ThemeName {
  if (typeof window === 'undefined') {
    return fallback;
  }
  try {
    const stored = window.localStorage.getItem(DESIGN_THEME_STORAGE_KEY);
    return isThemeName(stored) ? stored : fallback;
  } catch {
    return fallback;
  }
}

function writeStoredTheme(theme: ThemeName) {
  try {
    window.localStorage.setItem(DESIGN_THEME_STORAGE_KEY, theme);
  } catch {
    // Ignore quota / private-mode failures.
  }
}

export interface DesignThemeContextValue {
  /** Active forced theme, if any. */
  forcedTheme: ThemeName | undefined;
  /** Update the design theme (no-op when forced). */
  setTheme: (theme: ThemeName) => void;
  /** Resolved design theme name (respects forcedTheme when set). */
  theme: ThemeName;
  /** Registered theme names. */
  themes: readonly ThemeName[];
}

const DesignThemeContext = React.createContext<DesignThemeContextValue | null>(null);

export interface DesignThemeProviderProps {
  children: React.ReactNode;
  /** Uncontrolled default theme name. */
  defaultTheme?: ThemeName;
  /** Force a theme for this tree (like next-themes `forcedTheme`). */
  forcedTheme?: ThemeName;
  /** Called when the theme changes (controlled or uncontrolled). */
  onThemeChange?: (theme: ThemeName) => void;
  /** Controlled theme name. */
  theme?: ThemeName;
}

export function DesignThemeProvider({
  children,
  theme: controlledTheme,
  defaultTheme = 'default',
  forcedTheme,
  onThemeChange,
}: DesignThemeProviderProps) {
  const [uncontrolledTheme, setUncontrolledTheme] = React.useState<ThemeName>(defaultTheme);

  // Restore persisted theme after mount (avoids SSR mismatch).
  React.useEffect(() => {
    if (controlledTheme !== undefined || forcedTheme) {
      return;
    }
    const stored = readStoredTheme(defaultTheme);
    setUncontrolledTheme(stored);
  }, [controlledTheme, defaultTheme, forcedTheme]);

  const theme = forcedTheme ?? controlledTheme ?? uncontrolledTheme;

  const setTheme = React.useCallback(
    (next: ThemeName) => {
      if (forcedTheme) {
        return;
      }
      if (controlledTheme === undefined) {
        setUncontrolledTheme(next);
        writeStoredTheme(next);
      }
      onThemeChange?.(next);
    },
    [controlledTheme, forcedTheme, onThemeChange],
  );

  const value = React.useMemo<DesignThemeContextValue>(
    () => ({
      theme,
      forcedTheme,
      setTheme,
      themes: THEME_NAMES,
    }),
    [theme, forcedTheme, setTheme],
  );

  return <DesignThemeContext.Provider value={value}>{children}</DesignThemeContext.Provider>;
}

export function useDesignTheme(): DesignThemeContextValue {
  const ctx = React.useContext(DesignThemeContext);
  if (!ctx) {
    throw new Error('useDesignTheme must be used within a DesignThemeProvider');
  }
  return ctx;
}

/** Optional hook that returns null outside a DesignThemeProvider. */
export function useDesignThemeOptional(): DesignThemeContextValue | null {
  return React.useContext(DesignThemeContext);
}
